<template>
  <div class="home">
    <!-- Hero Section -->
    <section class="hero-section py-5">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-6">
            <h1 class="display-4 fw-bold text-white mb-4">
              Master Spring Boot Development
            </h1>
            <p class="lead text-white mb-4">
              Comprehensive tutorials, production-ready code samples, and expert insights 
              to accelerate your Spring Boot journey from concept to deployment.
            </p>
            <div class="d-flex gap-3">
              <RouterLink to="/samples" class="btn btn-light btn-lg">
                Browse Code Samples
              </RouterLink>
              <RouterLink to="/services" class="btn btn-outline-light btn-lg">
                Our Services
              </RouterLink>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="code-preview p-4 bg-dark rounded">
              <pre class="text-light mb-0"><code>@RestController
@RequestMapping("/api/users")
public class UserController {
    
    @Autowired
    private UserService userService;
    
    @GetMapping
    public ResponseEntity&lt;List&lt;User&gt;&gt; getUsers() {
        return ResponseEntity.ok(
            userService.findAll()
        );
    }
}</code></pre>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="py-5">
      <div class="container">
        <div class="row">
          <div class="col-lg-4 mb-4">
            <div class="card h-100 shadow-sm">
              <div class="card-body text-center p-4">
                <div class="feature-icon mb-3">
                  <i class="bi bi-code-square fs-1 text-primary"></i>
                </div>
                <h5 class="card-title">120+ Code Samples</h5>
                <p class="card-text">
                  Comprehensive collection of Spring Boot examples covering all major features
                  and use cases for modern development.
                </p>
              </div>
            </div>
          </div>
          
          <div class="col-lg-4 mb-4">
            <div class="card h-100 shadow-sm">
              <div class="card-body text-center p-4">
                <div class="feature-icon mb-3">
                  <i class="bi bi-mortarboard fs-1 text-primary"></i>
                </div>
                <h5 class="card-title">Expert Knowledge</h5>
                <p class="card-text">
                  Learn from industry best practices and proven patterns used in 
                  production Spring Boot applications.
                </p>
              </div>
            </div>
          </div>
          
          <div class="col-lg-4 mb-4">
            <div class="card h-100 shadow-sm">
              <div class="card-body text-center p-4">
                <div class="feature-icon mb-3">
                  <i class="bi bi-lightning fs-1 text-primary"></i>
                </div>
                <h5 class="card-title">Quick Start</h5>
                <p class="card-text">
                  Get up and running quickly with our step-by-step guides and 
                  ready-to-use project templates.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Categories Section -->
    <section class="py-5 bg-light">
      <div class="container">
        <div class="row">
          <div class="col-12 text-center mb-5">
            <h2 class="display-5 fw-bold">Code Sample Categories</h2>
            <p class="lead">Explore our organized collection of Spring Boot examples</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-md-6 col-lg-3 mb-4" v-for="category in categories" :key="category.name">
            <RouterLink :to="`/samples/${category.id}`" class="text-decoration-none">
              <div class="category-card p-4 bg-white rounded shadow-sm h-100">
                <h6 class="fw-bold text-primary mb-2">{{ category.name }}</h6>
                <p class="small text-muted mb-3">{{ category.description }}</p>
                <div class="small">
                  <strong>{{ category.samples }}</strong> samples
                </div>
              </div>
            </RouterLink>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { RouterLink } from 'vue-router'

const categories = ref([
  {
    id: 'getting-started',
    name: 'Getting Started',
    description: 'Basic setup and configuration examples',
    samples: 15
  },
  {
    id: 'web-development',
    name: 'Web Development',
    description: 'REST APIs, controllers, and web MVC',
    samples: 25
  },
  {
    id: 'data-access',
    name: 'Data Access',
    description: 'JPA, repositories, and database integration',
    samples: 20
  },
  {
    id: 'security',
    name: 'Security',
    description: 'Authentication, authorization, and security',
    samples: 18
  },
  {
    id: 'testing',
    name: 'Testing',
    description: 'Unit tests, integration tests, and mocking',
    samples: 16
  },
  {
    id: 'microservices',
    name: 'Microservices',
    description: 'Service discovery, config, and communication',
    samples: 12
  },
  {
    id: 'production',
    name: 'Production',
    description: 'Monitoring, logging, and deployment',
    samples: 10
  },
  {
    id: 'advanced-topics',
    name: 'Advanced Topics',
    description: 'Custom configurations and advanced patterns',
    samples: 8
  }
])
</script>

<style scoped>
.hero-section {
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  min-height: 60vh;
  display: flex;
  align-items: center;
}

.code-preview {
  font-size: 0.9rem;
  line-height: 1.4;
}

.feature-icon {
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.category-card {
  transition: transform 0.2s;
  border: 1px solid #e9ecef;
}

.category-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1) !important;
}

.card {
  border: none;
  transition: transform 0.2s;
}

.card:hover {
  transform: translateY(-2px);
}
</style>